/**
 * ARKO Web-Artikel-Fetch — Portal-Client (URL-Erkennung, Fetch-Webhook).
 */

import {
  ARKO_WEB_ARTICLE_FETCH_URL,
  hasPortalAuthzToken,
  portalAuthzHeaders,
  resolveOnlyofficeIssueJwtUrl,
} from '../../constants';

export interface WebArticleFetchResult {
  ok: boolean;
  url: string;
  host: string;
  title?: string;
  text?: string;
  truncated?: boolean;
  paywall_hint?: string;
  fetched_at?: string;
  chars?: number;
  allowed?: boolean;
  error?: string;
}

export interface WebArticleRequest {
  url: string;
  userIntent?: string;
  displayText: string;
}

const URL_RE = /https?:\/\/[^\s<>"']+/i;
const INTENT_SEP_RE = /\s*(?:—|–|-)\s*(.+)$/;

export function extractFirstUrl(text: string): string | null {
  const m = String(text || '').trim().match(URL_RE);
  return m ? m[0].replace(/[.,;:!?)]+$/, '') : null;
}

/** Erkennt URL im Chat-Input; optionaler Intent nach Gedankenstrich. */
export function detectWebArticleRequest(text: string): WebArticleRequest | null {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const url = extractFirstUrl(raw);
  if (!url) return null;
  const withoutUrl = raw.replace(url, '').trim();
  let userIntent: string | undefined;
  const sep = withoutUrl.match(INTENT_SEP_RE);
  if (sep && sep[1]?.trim()) {
    userIntent = sep[1].trim();
  } else if (withoutUrl && !/^https?:\/\//i.test(withoutUrl)) {
    userIntent = withoutUrl;
  }
  return { url, userIntent, displayText: raw };
}

export function buildWebArticleDocumentContext(article: WebArticleFetchResult): string {
  const lines = [`Quelle: ${article.url} (${article.host})`];
  if (article.title?.trim()) lines.push(`Titel: ${article.title.trim()}`);
  lines.push('', String(article.text || '').trim());
  return lines.join('\n');
}

export function webArticleMetaForTrace(article: WebArticleFetchResult): {
  url: string;
  host: string;
  allowed: boolean;
  chars: number;
} {
  return {
    url: article.url,
    host: article.host,
    allowed: article.allowed !== false,
    chars: article.chars ?? String(article.text || '').length,
  };
}

export function formatWebArticlePreview(article: WebArticleFetchResult): string {
  const title = article.title?.trim();
  const snippet = String(article.text || '').slice(0, 320).trim();
  const parts = [
    `Artikel geladen von ${article.host}`,
    title ? `„${title}"` : null,
    snippet ? `${snippet}${(article.text || '').length > 320 ? '…' : ''}` : null,
    article.paywall_hint || null,
    article.truncated ? 'Hinweis: Text wurde serverseitig gekürzt.' : null,
  ].filter(Boolean);
  return parts.join('\n\n');
}

const FETCH_TIMEOUT_MS = 30_000;

export async function fetchWebArticle(url: string): Promise<WebArticleFetchResult> {
  if (!ARKO_WEB_ARTICLE_FETCH_URL) {
    return { ok: false, url, host: '', error: 'Web-Artikel-Fetch ist nicht konfiguriert.' };
  }
  if (!hasPortalAuthzToken()) {
    return { ok: false, url, host: '', error: 'Bitte neu anmelden (kein gültiges Portal-Token).' };
  }
  const fetchUrl = resolveOnlyofficeIssueJwtUrl(ARKO_WEB_ARTICLE_FETCH_URL);
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(fetchUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...portalAuthzHeaders(fetchUrl),
      },
      body: JSON.stringify({ url }),
      signal: controller.signal,
    });
    const data = (await res.json()) as WebArticleFetchResult;
    if (!res.ok || !data.ok) {
      return {
        ok: false,
        url,
        host: data.host || '',
        allowed: data.allowed,
        error: data.error || `Fetch fehlgeschlagen (HTTP ${res.status})`,
      };
    }
    return data;
  } catch (err) {
    const aborted = err instanceof DOMException && err.name === 'AbortError';
    return {
      ok: false,
      url,
      host: '',
      error: aborted ? 'Zeitüberschreitung beim Laden des Artikels.' : 'Artikel konnte nicht geladen werden.',
    };
  } finally {
    window.clearTimeout(timer);
  }
}
